import { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Grid,
  TextField,
  IconButton,
  Snackbar,
  Tooltip,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import axios from "../../config/axios";
import { getUserData } from "../../utils/utilities";
import { Close } from "@mui/icons-material";
import ColorPicker from "../../components/ColorPicker";
import { useAuth } from "../../hooks/auth";

const UserProfile = () => {
  const auth = useAuth();
  const [userData, setUserData] = useState({});
  const [alert, setAlert] = useState({});
  const [validations, setValidations] = useState({});
  const [btnLoader, setBtnLoader] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logo, setLogo] = useState(null);
  const logoFileInputRef = useRef(null);
  const [color, setColor] = useState({ r: "240", g: "240", b: "240", a: "1" });
  let currentUser = getUserData();

  // other functions
  const closeAlertMessage = () => {
    setAlert({});
  };

  const handleChange = (field, value) => {
    setUserData((prevState) => ({
      ...prevState,
      [`${field}`]: value,
    }));

    // Remove validation error for the field that changes
    setValidations((prevValidations) => {
      const newValidations = { ...prevValidations };
      delete newValidations[field]; // Remove the error message for this field
      return newValidations;
    });
  };

  // API Services
  const getUserDetails = () => {
    const params = {
      id: currentUser.id,
    };

    axios
      .get("/user/details", { params })
      .then((res) => {
        if (res.status === 200) {
          setUserData(res.data.data || {});
          setLogo(res.data.data?.logo || null);
          setColor(res.data.data?.color);
          if (res.data.data?.logo) {
            setLogoPreview(
              `${process.env.REACT_APP_IMAGE_URL}images/company/logo/${res.data.data.logo}`
            );
          }
        }
      })
      .catch((err) => console.log(err));
  };

  const updateUserDetails = () => {
    setBtnLoader(true);

    if (currentUser.role == "Company" && !userData.name) {
      setValidations((preValidations) => {
        return {
          ["name"]: "Company name can not be empty",
          ...preValidations,
        };
      });
    }

    if (Object.keys(validations).length > 0) return;

    const params = {
      id: currentUser.id,
    };

    const formData = new FormData();

    if (currentUser.role == "Company") {
      formData.append("name", userData.name || "");
      formData.append("logo", logo || "");
      formData.append("color", JSON.stringify(color) || "");
    }
    formData.append("email", userData.email || "");
    formData.append("person_name", userData.person_name || "");

    axios
      .put("user/update", formData, { params })
      .then((res) => {
        if (res.status === 200) {
          if (res.data.status) {
            setAlert({
              variant: "success",
              msg: res.data.message,
              status: true,
            });
            const updatedUser = res.data.data;

            let localUser = localStorage.getItem("cvg_user");

            if (localUser) {
              localUser = JSON.parse(localUser);

              localUser.name = updatedUser.name;
              if (updatedUser.logo) localUser.logo = updatedUser.logo;

              localStorage.setItem("cvg_user", JSON.stringify(localUser));
              auth.login(JSON.stringify(localUser));
              setUserData(res.data.data || {});
            }
            setLogo(res.data.data?.logo || null);
            setValidations({});
            if (res.data.data?.logo) {
              setLogoPreview(
                `${process.env.REACT_APP_IMAGE_URL}images/company/logo/${res.data.data.logo}`
              );
            }
            getUserDetails();
          } else {
            if (res.data.type === "validation") {
              res.data.error?.forEach((err) => {
                setValidations((preValidations) => {
                  return { [err.field]: err.message, ...preValidations };
                });
              });
            }
            if (res.data.type === "error") {
              setAlert({
                variant: "error",
                msg: res.data.error?.message,
                status: true,
              });
            }
          }
          setBtnLoader(false);
        }
      })
      .catch((err) => {
        setBtnLoader(false);
        console.log(err);
      });
  };

  useEffect(() => {
    getUserDetails();
  }, []);

  const openFileExplorer = () => {
    if (logoFileInputRef && logoFileInputRef.current) {
      logoFileInputRef.current.click();
    }
  };

  const generateLogoPreview = (e) => {
    if (e.target.files.length > 0) {
      if (["image/png", "image/jpeg"].includes(e.target.files[0].type)) {
        setLogoPreview(URL.createObjectURL(e.target.files[0]));
        setLogo(e.target.files[0]);

        // Remove validation error for logo
        setValidations((prevValidations) => {
          const newValidations = { ...prevValidations };
          delete newValidations.logo; // Remove the error message for logo
          return newValidations;
        });
      } else {
        setLogoPreview(null);
        setLogo(null);
        setValidations((preValidations) => {
          return {
            ...preValidations,
            logo: "Invalid Extension. Only png, jpg, jpeg are allowed",
          };
        });
      }
    } else {
      setLogoPreview(null);
      setLogo(null);
    }
  };

  return (
    <Grid container rowSpacing={2}>
      <Grid xs={12} sm={12} md={12} lg={12} xl={12} item>
        <Typography variant="body1" sx={{ fontSize: "20px" }} color="#474747">
          User Profile
        </Typography>
      </Grid>
      <Grid xs={12} sm={12} md={12} lg={12} xl={12} item>
        <Paper elevation={0}>
          <Box sx={{ padding: "30px" }}>
            <Grid container spacing={3}>
              <Grid xs={12} sm={12} md={6} lg={6} xl={6} item>
                <TextField
                  label={currentUser.role == "Company" ? "Person Name" : "Name"}
                  placeholder="Name"
                  size="small"
                  value={userData.person_name || ""}
                  onChange={(e) => handleChange("person_name", e.target.value)}
                  fullWidth
                  error={!!validations.person_name}
                  helperText={validations.person_name || " "}
                />
              </Grid>
              <Grid xs={12} sm={12} md={6} lg={6} xl={6} item>
                <TextField
                  size="small"
                  fullWidth
                  label="Email"
                  value={userData.email || ""}
                  onChange={(e) => handleChange("email", e.target.value)}
                  error={!!validations.email}
                  helperText={validations.email || ""}
                />
              </Grid>
              {currentUser.role == "Company" && (
                <Grid xs={12} sm={12} md={6} lg={6} xl={6} item>
                  <TextField
                    autoFocus
                    size="small"
                    fullWidth
                    label="Company Name"
                    value={userData.name || ""}
                    onChange={(e) => handleChange("name", e.target.value)}
                    error={!!validations.name}
                    helperText={validations.name || ""}
                  />
                </Grid>
              )}
              {currentUser.role == "Company" && (
                <Grid xs={12} sm={12} md={6} lg={6} xl={6} item>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span>Logo</span>
                    <Tooltip title="Logo Background">
                      <Box>
                        <ColorPicker
                          color={color}
                          setColor={setColor}
                          popoverStyle={{ top: 10, right: 80 }}
                          colorBoxStyle={{ height: 20 }}
                        />
                      </Box>
                    </Tooltip>
                  </Box>
                  <div
                    className="logo_container"
                    style={{
                      backgroundColor: `rgba(${color?.r}, ${color?.g},${color?.b}, ${color?.a})`,
                    }}
                  >
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="company logo"
                        height={100}
                        width={226}
                        style={{ cursor: "pointer", objectFit: "contain" }}
                        onClick={openFileExplorer}
                      />
                    ) : (
                      <div className="logo" onClick={openFileExplorer}>
                        Select Logo
                      </div>
                    )}
                  </div>
                  {validations.logo ? (
                    <span style={{ fontSize: "13px", color: "red" }}>
                      {validations.logo}
                    </span>
                  ) : (
                    ""
                  )}
                  <TextField
                    id="logo"
                    placeholder="select logo"
                    type="file"
                    size="small"
                    onChange={generateLogoPreview}
                    fullWidth
                    sx={{ display: "none" }}
                    inputRef={logoFileInputRef}
                    error={!!validations.logo}
                    helperText={validations.logo || " "}
                  />
                </Grid>
              )}

              <Grid xs={12} sm={12} md={12} lg={12} xl={12} item>
                <LoadingButton variant="contained" onClick={updateUserDetails}>
                  Update
                </LoadingButton>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={alert.status}
        onClose={closeAlertMessage}
        message={alert.msg}
        key={"system-message"}
        autoHideDuration={2000}
      >
        <Alert severity={alert.variant}>
          {alert.msg}
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            sx={{ ml: 2 }}
            onClick={closeAlertMessage}
          >
            <Close fontSize="small" />
          </IconButton>
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default UserProfile;
